/**
 * The streamed shelf (POST /api/search/shelf): the shelf judge's picks reach
 * the shopper one card at a time, the moment each pick's line is written, then
 * the lookup's whole result. Pure and client-safe.
 *
 * Every line is one JSON object:
 *  - `card`: one product the judge picked, with its page (the hit without its
 *    products, marked `judged`). The product carries `judgedRank`, its place
 *    on the shelf; cards arrive in that order and never move.
 *  - `done`: exactly what the server action `searchPagesWithStatus` returns.
 *    The shelf keeps the cards it showed; `done` adds what the stream could
 *    not (unjudged shelves, a cache hit, untranslated pages, the focus shelf).
 *  - `error`: the lookup failed; the caller treats it as a degraded result.
 */
import type { AlgoliaHit, SearchProductPreview } from 'shared/src/types/search.type';
import type { SearchPagesResult } from '@/server/search/get-algolia-search';
import { readNdjsonLines } from '@/lib/ndjson';

export const SHELF_STREAM_ENDPOINT = '/api/search/shelf';

/** A product the judge picked, with the page it is on. */
export type ShelfStreamCard = { hit: AlgoliaHit; product: SearchProductPreview };

export type ShelfStreamLine =
  | ({ type: 'card' } & ShelfStreamCard)
  | { type: 'done'; result: SearchPagesResult }
  | { type: 'error' };

/** Parse one NDJSON line. Null for anything malformed. */
export function parseShelfStreamLine(line: string): ShelfStreamLine | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const value = parsed as Record<string, unknown>;
  if (value.type === 'card') {
    const hit = value.hit as AlgoliaHit | undefined;
    const product = value.product as SearchProductPreview | undefined;
    if (!hit || typeof hit.id !== 'string' || !product || typeof product.title !== 'string') return null;
    return { type: 'card', hit, product };
  }
  if (value.type === 'done') {
    const result = value.result as SearchPagesResult | undefined;
    if (!result || !Array.isArray(result.hits)) return null;
    return { type: 'done', result };
  }
  if (value.type === 'error') return { type: 'error' };
  return null;
}

/**
 * The shelf's hits so far: one hit per page, in the order its first card
 * arrived, each holding its cards in arrival order. Pure: returns new arrays.
 */
export function addStreamCard(hits: AlgoliaHit[], card: ShelfStreamCard): AlgoliaHit[] {
  const index = hits.findIndex((hit) => hit.id === card.hit.id);
  if (index === -1) return [...hits, { ...card.hit, judged: true, products: [card.product] }];
  const current = hits[index];
  if ((current.products ?? []).some((product) => product.title === card.product.title)) return hits;
  const next = [...hits];
  next[index] = { ...current, products: [...(current.products ?? []), card.product] };
  return next;
}

/** The lookup's arguments as the search bar holds them (shelfLookupFor). */
export type ShelfLookupArgs = {
  currentHref: string;
  searchTerm: string;
  originalUserQuery?: string;
  locale: { path: string };
  isDone?: boolean;
  subregionPref?: { code: string; priority: string } | null;
  focusTerms?: string[];
  shelfTurns?: unknown;
};

/**
 * Run the shelf lookup through the stream: each card goes to `onCard` as it
 * arrives, and the promise resolves with the lookup's whole result (`done`).
 * Rejects when the route fails or the stream ends without a result, so the
 * caller can fall back to the server action. Client-side.
 */
export async function streamShelfLookup(
  args: ShelfLookupArgs,
  options: { onCard: (card: ShelfStreamCard) => void; signal?: AbortSignal },
): Promise<SearchPagesResult> {
  const response = await fetch(SHELF_STREAM_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      currentHref: args.currentHref,
      searchTerm: args.searchTerm,
      ...(args.originalUserQuery ? { originalUserQuery: args.originalUserQuery } : {}),
      localePath: args.locale.path,
      isDone: !!args.isDone,
      ...(args.subregionPref ? { subregionPref: args.subregionPref } : {}),
      ...(args.focusTerms?.length ? { focusTerms: args.focusTerms } : {}),
      ...(args.shelfTurns !== undefined ? { shelfTurns: args.shelfTurns } : {}),
    }),
    signal: options.signal,
  });
  if (!response.ok || !response.body) throw new Error(`shelf stream answered ${response.status}`);
  let result: SearchPagesResult | null = null;
  let failed = false;
  await readNdjsonLines(response.body, (text) => {
    const line = parseShelfStreamLine(text);
    if (!line) return;
    if (line.type === 'card') options.onCard({ hit: line.hit, product: line.product });
    else if (line.type === 'done') result = line.result;
    else failed = true;
  });
  if (!result) throw new Error(failed ? 'shelf lookup failed' : 'shelf stream ended without a result');
  return result;
}
